import React, { useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Container,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { palette } from "../../themes/theme";
import { fetchAllUsers, deleteUser } from "../../redux/thunk/userThunk";
import ConfirmationModal from "../../component/Modal/ConfirmationModal";
import {
  TableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledHeaderCell,
  PaginationContainer,
  ActionButtonsContainer,
  HeaderContainer,
  AddUserButton,
  LoadingContainer,
} from "./styled";
import { ArrowBack, ArrowBackIos } from "@mui/icons-material";
import crimeRiskLogo from "../../assets/images/CrimeRisk.AI Final Logo 2.png";

const UserManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { usersList, usersListLoading } = useSelector((state) => state.user);

  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  // Fetch users on component mount
  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  const handleEdit = (user) => {
    navigate("/admin/user/edit", { state: { user } });
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      setDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleAddUser = () => {
    navigate("/admin/user/add");
  };

  const navigateToMap = () => {
    navigate("/map");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "firstName",
        header: "First Name",
        size: 120,
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
        size: 120,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 220,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        size: 130,
        cell: ({ row }) => row.original.phone || "N/A",
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 180,
        cell: ({ row }) => {
          const createdAt = row.original.createdAt;
          if (!createdAt) return "Never";
          return new Date(createdAt).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            // hour: "2-digit",
            // minute: "2-digit",
          });
        },
      },
      {
        accessorKey: "endDate",
        header: "End Date   ",
        size: 180,
        cell: ({ row }) => {
          const endDate = row.original.endDate;
          if (!endDate) return "Never";
          return new Date(endDate).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            // hour: "2-digit",
            // minute: "2-digit",
          });
        },
      },
      {
        accessorKey: "roles.name",
        header: "Role",
        size: 120,
        cell: ({ row }) => {
          const roleName = row.original.role?.name || "N/A";
          const roleColors = {
            superadmin: palette.background.danger,
            user: palette.secondary.main,
          };
          return (
            <Chip
              label={roleName.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: roleColors["user"] || roleColors["user"],
                color: palette.text.white,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          );
        },
      },
      {
        accessorKey: "selectedCountries",
        header: "Countries",
        size: 150,
        cell: ({ row }) => {
          const countries = row.original.selectedCountries;
          if (!countries || countries.length === 0) {
            return <Typography variant="body2">N/A</Typography>;
          }
          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {countries.map((country, index) => (
                <Chip
                  key={index}
                  label={country.toUpperCase()}
                  size="small"
                  sx={{
                    backgroundColor: palette.primary.main,
                    color: palette.text.white,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              ))}
            </Box>
          );
        },
      },
      {
        accessorKey: "accountVerified",
        header: "Verified",
        size: 100,
        cell: ({ row }) =>
          row.original.accountVerified ? (
            <CheckCircleIcon
              sx={{ color: palette.background.success, fontSize: 20 }}
            />
          ) : (
            <CancelIcon
              sx={{ color: palette.background.danger, fontSize: 20 }}
            />
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        cell: ({ row }) => (
          <Chip
            label={row.original.status ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: row.original.status
                ? palette.background.success
                : palette.background.danger,
              color: palette.text.white,
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        ),
      },
      {
        accessorKey: "companyName",
        header: "Company",
        size: 150,
        cell: ({ row }) => row.original.companyName || "N/A",
      },

      {
        id: "actions",
        header: "Actions",
        size: 120,
        cell: ({ row }) => {
          const roleName = String(row.original.roles?.name || "").toLowerCase();
          // Do not show edit/delete for superadmin users
          if (roleName === "superadmin") return null;

          return (
            <ActionButtonsContainer>
              <IconButton
                onClick={() => handleEdit(row.original)}
                sx={{
                  color: palette.primary.main,
                  "&:hover": {
                    backgroundColor: `${palette.primary.main}20`,
                  },
                }}
                size="small"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(row.original)}
                sx={{
                  color: palette.background.danger,
                  "&:hover": {
                    backgroundColor: `${palette.background.danger}20`,
                  },
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </ActionButtonsContainer>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: usersList || [],
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  // Show loading state
  if (usersListLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LoadingContainer>
          <CircularProgress sx={{ color: palette.primary.main }} size={60} />
          <Typography variant="h6" sx={{ color: palette.text.primary, mt: 2 }}>
            Loading users...
          </Typography>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <>
      {/* Logo at top left corner with absolute positioning */}
      <Box
        sx={{
          position: "fixed",
          top: 10,
          left: 25,
          zIndex: 1000,
          "& img": {
            width: "200px",
            cursor: "pointer",
            "@media (max-width: 1024px)": {
              width: "150px",
            },
          },
        }}
      >
        <img src={crimeRiskLogo} alt="CrimeRisk.ai" onClick={navigateToMap} />
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          width: "100%",
          paddingTop: "10vh",
          minHeight: "60vh",
          "@media (max-width: 1024px)": {
            paddingTop: "15vh",
          },
        }}
      >
        <HeaderContainer>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: palette.text.dark,
                fontWeight: 600,
                mb: 1,
              }}
            >
              <ArrowBackIos
                onClick={navigateToMap}
                sx={{
                  cursor: "pointer",
                  mr: 1,
                  "&:hover": { color: palette.primary.main },
                }}
              />
              User Management
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: palette.text.primary,
              }}
            >
              Manage your users and their roles
            </Typography>
          </Box>
          <AddUserButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
          >
            Add User
          </AddUserButton>
        </HeaderContainer>

        <TableContainer component={Paper}>
          <StyledTable>
            <StyledTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <StyledTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <StyledHeaderCell
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        width: header.getSize(),
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() === "asc" && " 🔼"}
                      {header.column.getIsSorted() === "desc" && " 🔽"}
                    </StyledHeaderCell>
                  ))}
                </StyledTableRow>
              ))}
            </StyledTableHead>
            <StyledTableBody>
              {table.getRowModel().rows.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={columns.length}
                    style={{ textAlign: "center", padding: "40px" }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: palette.text.primary }}
                    >
                      No users found
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <StyledTableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>

          <PaginationContainer>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                sx={{
                  borderColor: palette.primary.main,
                  color: palette.primary.main,
                  "&:hover": {
                    borderColor: palette.primary.main,
                    backgroundColor: `${palette.primary.main}10`,
                  },
                }}
              >
                {"<<"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                sx={{
                  borderColor: palette.primary.main,
                  color: palette.primary.main,
                  "&:hover": {
                    borderColor: palette.primary.main,
                    backgroundColor: `${palette.primary.main}10`,
                  },
                }}
              >
                {"<"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                sx={{
                  borderColor: palette.primary.main,
                  color: palette.primary.main,
                  "&:hover": {
                    borderColor: palette.primary.main,
                    backgroundColor: `${palette.primary.main}10`,
                  },
                }}
              >
                {">"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                sx={{
                  borderColor: palette.primary.main,
                  color: palette.primary.main,
                  "&:hover": {
                    borderColor: palette.primary.main,
                    backgroundColor: `${palette.primary.main}10`,
                  },
                }}
              >
                {">>"}
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ color: palette.text.primary }}>
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount() || 1}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.text.primary }}>
                Total: {usersList?.length || 0} users
              </Typography>
            </Box>
          </PaginationContainer>
        </TableContainer>

        <ConfirmationModal
          open={deleteModalOpen}
          handleClose={handleDeleteCancel}
          onSave={handleDeleteConfirm}
          loading={deleteLoading}
          headerTitle="Delete User"
          description={
            userToDelete
              ? `Are you sure you want to delete ${userToDelete.firstName} ${userToDelete.lastName} (${userToDelete.email})? This action cannot be undone.`
              : "Are you sure you want to delete this user?"
          }
        />
      </Container>
    </>
  );
};

export default UserManagement;
